import { FunctionComponent, useCallback, useContext, useMemo, useState } from "react";
import { Theme, makeStyles, AppBar, Toolbar, Grid, Typography, useTheme } from "@material-ui/core";

import { TopBarActions } from "./top-bar-actions";
import { ORANGE_COLOR } from "../../theme/theme";
import { NutritionContext } from "../../pages/nutrition/context/nutrition-context";
import { useTranslation } from "react-i18next";
import { AssetManager } from "../../theme/asset-manager";
import { TopBarAccordion } from "./top-bar-accordion/top-bar-accordion";
import { useMediaQuery } from "@mui/material";

export const APP_BAR_HEIGHT = 76;
const APP_BAR_COLOR = "transparent";
const LOGO_WIDTH = 200;

interface ITopBarStyleProps {
  cursor?: string;
  accordionOpen: boolean;
}
const useStyles = makeStyles((theme: Theme) => ({
  sectionContainer: {
    height: "100%",
    position: "relative",
  },
  appBar: {
    height: APP_BAR_HEIGHT,
    padding: 0,
    backgroundColor: APP_BAR_COLOR,
    position: "relative",
  },
  toolBar: {
    height: APP_BAR_HEIGHT,
    padding: 0,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    [theme.breakpoints.up("md")]: {
      height: APP_BAR_HEIGHT,
      padding: 0,
      paddingLeft: theme.spacing(14),
      paddingRight: theme.spacing(14),
    },
  },
  logo: {
    backgroundColor: theme.palette.background.default,
    boxShadow: `0px 0px 40px 18px ${theme.palette.background.default}`,
    fontWeight: 900,
    cursor: ({ cursor }: ITopBarStyleProps) => cursor,
  },
  accountIcon: {
    width: APP_BAR_HEIGHT * 0.5,
    height: APP_BAR_HEIGHT * 0.5,
  },
  iconButton: {
    color: theme.palette.common.black,
  },
  demoLabel: {
    backgroundColor: ORANGE_COLOR,
    borderRadius: 4,
    position: "absolute",
    bottom: 0,
    right: 0,
    transform: "translate(-10%, -10%)",
    fontSize: 14,
    fontWeight: 700,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  accordionContainer: {
    position: "absolute",
    top: "100%",
  },
}));

interface ITopBarProps {
  hideActions?: boolean;
  onLogoClick?: () => void;
}

export const TopBar: FunctionComponent<ITopBarProps> = (props) => {
  const { t } = useTranslation();
  const { hideActions, onLogoClick } = props;
  const cursor = useMemo(() => (onLogoClick ? "pointer" : "initial"), [onLogoClick]);
  const { isDemo } = useContext(NutritionContext);
  const [accordionOpen, setAccordionOpen] = useState<boolean>(false);
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));

  const isAccordionOpen = useMemo(() => {
    if (isMd && accordionOpen) {
      setAccordionOpen(false);
    }
    return accordionOpen && !isMd;
  }, [accordionOpen, isMd]);
  const handleMenuButton = useCallback(() => {
    setAccordionOpen(!accordionOpen);
  }, [setAccordionOpen, accordionOpen]);

  const classes = useStyles({ cursor, accordionOpen });
  return (
    <>
      <AppBar color="primary" position="static" elevation={0} className={classes.appBar}>
        <Toolbar className={classes.toolBar}>
          <Grid container justify="space-between">
            <Grid item>
              <Grid container alignItems="center" className={classes.sectionContainer}>
                <Grid item onClick={onLogoClick}>
                  <Typography className={classes.logo}>
                    <img src={AssetManager.Logo.DigitalTwin} alt="" width={LOGO_WIDTH} />
                  </Typography>
                </Grid>
                {isDemo && (
                  <Grid item>
                    <Typography className={classes.demoLabel}>{t("general.demo")}</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <Grid container alignItems="center" className={classes.sectionContainer}>
                {!hideActions && (
                  <TopBarActions accordionOpen={isAccordionOpen} onMenuButtonClicked={handleMenuButton} />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>
        <Grid item className={classes.accordionContainer}>
          <TopBarAccordion open={isAccordionOpen} onClose={() => setAccordionOpen(false)} />
        </Grid>
      </AppBar>
    </>
  );
};
